const sortableListItemKinds = [
	"design",
	"lineworks",
	"simaLine",
	"pointcloud",
	"reference-topology",
	"unknown",
] as const;

export type SortableListItemKind = (typeof sortableListItemKinds)[number];

export interface SortableListItemRef {
	kind: SortableListItemKind;
	id: string;
	sortKey: Date | null;
}

export type SessionOrderEntryKey = `${SortableListItemKind}:${string}`;

const sortableListItemKindSet: ReadonlySet<string> = new Set(
	sortableListItemKinds,
);

function parseDateValue(value: unknown): Date | null {
	if (!value) {
		return null;
	}

	if (value instanceof Date) {
		return Number.isNaN(value.getTime()) ? null : value;
	}

	const candidate = new Date(String(value));
	return Number.isNaN(candidate.getTime()) ? null : candidate;
}

function getItemKind(item: unknown): SortableListItemKind {
	const typedItem = (item ?? {}) as Record<string, unknown>;
	const rawType =
		typeof typedItem.getType === "function" ? typedItem.getType() : undefined;

	if (typeof rawType !== "string") {
		return "unknown";
	}

	return sortableListItemKindSet.has(rawType)
		? (rawType as SortableListItemKind)
		: "unknown";
}

function getItemId(item: unknown): string {
	const typedItem = (item ?? {}) as Record<string, unknown>;

	if (typeof typedItem.getId === "function") {
		return String(typedItem.getId());
	}

	return "";
}

function getSortKeyDate(
	item: unknown,
	kind: SortableListItemKind,
): Date | null {
	const typedItem = (item ?? {}) as Record<string, unknown>;
	if (kind === "unknown") {
		return null;
	}

	if (kind === "lineworks" || kind === "simaLine") {
		if (typeof typedItem.getCreatedAt === "function") {
			return parseDateValue(typedItem.getCreatedAt());
		}
		return null;
	}

	let selectedDate: unknown = null;
	let selectedTime: unknown = null;

	if (kind === "reference-topology") {
		selectedDate = typedItem.selectedDate;
		selectedTime = typedItem.selectedTime;
	} else {
		selectedDate =
			typeof typedItem.getSelectedDate === "function"
				? typedItem.getSelectedDate()
				: null;
		selectedTime =
			typeof typedItem.getSelectedTime === "function"
				? typedItem.getSelectedTime()
				: null;
	}
	if (!selectedDate) return null;

	return parseDateValue(
		`${String(selectedDate)}T${selectedTime ? String(selectedTime) : "00:00:00"}`,
	);
}

function compareDateAsc(a: Date | null, b: Date | null): number {
	if (!a && !b) return 0;
	if (!a) return 1;
	if (!b) return -1;

	if (a > b) return 1;
	if (a < b) return -1;
	return 0;
}

export function toSortableListItemRef(item: unknown): SortableListItemRef {
	const kind = getItemKind(item);

	return {
		kind,
		id: getItemId(item),
		sortKey: getSortKeyDate(item, kind),
	};
}

export function toSessionOrderEntryKey(item: unknown): SessionOrderEntryKey {
	const ref = toSortableListItemRef(item);
	return `${ref.kind}:${ref.id}`;
}

export function compareDateByType(a: unknown, b: unknown): number {
	const refA = toSortableListItemRef(a);
	const refB = toSortableListItemRef(b);

	const byDate = compareDateAsc(refA.sortKey, refB.sortKey);
	if (byDate !== 0) return byDate;

	return refA.id.localeCompare(refB.id);
}
